/**
 * PresetSidebar Component
 * 
 * A ready-to-use sidebar component that accepts a configuration object
 * and renders a complete sidebar with all features included.
 */

import * as React from "react"
import { 
  Home, 
  GitBranch, 
  Target, 
  BrainCircuit, 
  Beaker, 
  GitCommit, 
  Users, 
  UserPlus, 
  BookOpen, 
  CheckSquare, 
  Award, 
  Shield, 
  CheckCircle,
  Circle,
  Settings,
  CreditCard,
  Bell,
  LogOut,
  User,
  Building,
  Folder,
  FileText,
  Calendar,
  Clock,
  Zap,
  Database,
  Code,
  Terminal,
  Monitor,
  Smartphone,
  Globe,
  Lock,
  Key,
  Search,
  Filter,
  BarChart,
  PieChart,
  TrendingUp,
  Activity,
  MessageCircle
} from "lucide-react"

import { cn } from "../../lib/utils"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarBranding,
  SidebarMenuBadge,
} from "../layouts/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "./avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu"

import type { SidebarConfig, PresetSidebarProps } from "@/types/sidebar"

// Comprehensive icon mapping for the preset sidebar
// Add any Lucide React icons you need here
const iconMap: Record<string, React.ComponentType<any>> = {
  // Navigation & UI
  Home,
  Settings,
  Search,
  Filter,
  User,
  Users,
  UserPlus,
  
  // Development & Code
  GitBranch,
  GitCommit,
  Code,
  Terminal,
  Database,
  
  // Business & Planning
  Target,
  Calendar,
  Clock,
  Building,
  Folder,
  FileText,
  
  // Quality & Testing
  Beaker,
  CheckSquare,
  Award,
  Shield,
  CheckCircle,
  
  // AI & Automation
  BrainCircuit,
  Zap,
  
  // Education & Growth
  BookOpen,
  
  // Devices & Platforms
  Monitor,
  Smartphone,
  Globe,
  
  // Security
  Lock,
  Key,
  
  // Analytics & Charts
  BarChart,
  PieChart,
  TrendingUp,
  Activity,
  
  // User Actions
  CreditCard,
  Bell,
  LogOut,
  MessageCircle,
  
  // Fallback
  Circle
}

// Icon helper
const getIconComponent = (iconName: string) => {
  return iconMap[iconName] || Circle
}

export const PresetSidebar = React.forwardRef<
  HTMLDivElement,
  PresetSidebarProps
>(({ config, currentUser, className, onNavigate, userMenuActions, ...props }, ref) => {
  // Use currentUser prop if provided, otherwise fall back to config.user
  const activeUser = currentUser || config.user;

  const handleNavigation = (href: string, item: any) => {
    if (onNavigate) {
      onNavigate(href, item)
    } else {
      // Default navigation behavior
      window.location.href = href
    }
  }

  return (
    <Sidebar ref={ref} className={cn("", className)} {...props}>
      <SidebarHeader>
        <SidebarBranding 
          companyName={config.brand.name}
          companySubtitle={config.brand.subtitle}
          logoUrl={config.brand.logo}
          icon={config.brand.icon ? getIconComponent(config.brand.icon) : undefined}
          size={config.brand.size}
        />
      </SidebarHeader>

      <SidebarContent>
        {/* Standalone Items */}
        {config.standaloneItems.length > 0 && (
          <SidebarGroup>
            <SidebarMenu>
              {config.standaloneItems.map((item) => {
                const IconComponent = getIconComponent(item.icon)
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.isActive}
                      onClick={() => handleNavigation(item.href, item)}
                    >
                      <a href={item.href} className="flex items-center justify-start gap-3 text-left">
                        <IconComponent className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate text-left">{item.label}</span>
                        {item.badge && (
                          <SidebarMenuBadge className="ml-auto shrink-0">
                            {item.badge.text}
                          </SidebarMenuBadge>
                        )}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {/* Navigation Groups */}
        {config.groups.map((group) => {
          const GroupIconComponent = getIconComponent(group.icon)
          return (
            <SidebarGroup key={group.id}>
              <SidebarGroupLabel className="flex items-center justify-start gap-2 text-left">
                <GroupIconComponent 
                  className={cn(
                    "h-4 w-4 shrink-0",
                    group.themeColor && `text-${group.themeColor}`
                  )} 
                />
                <span className="flex-1 truncate text-left">{group.label}</span>
              </SidebarGroupLabel>
              <SidebarMenu>
                {group.items.map((item) => {
                  const ItemIconComponent = getIconComponent(item.icon)
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={item.isActive}
                        onClick={() => handleNavigation(item.href, item)}
                      >
                        <a href={item.href} className="flex items-center justify-start gap-3 text-left">
                          <ItemIconComponent className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate text-left">{item.label}</span>
                          {item.badge && (
                            <SidebarMenuBadge className="ml-auto shrink-0">
                              {item.badge.text}
                            </SidebarMenuBadge>
                          )}
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground justify-start text-left"
                >
                  <Avatar className="h-8 w-8 rounded-lg shrink-0">
                    <AvatarImage 
                      src={activeUser.avatar} 
                      alt={activeUser.name} 
                    />
                    <AvatarFallback className="rounded-lg">
                      {activeUser.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                    <span className="truncate font-semibold text-left">
                      {activeUser.name}
                    </span>
                    <span className="truncate text-xs text-left">
                      {activeUser.role}
                    </span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg shrink-0">
                      <AvatarImage 
                        src={activeUser.avatar} 
                        alt={activeUser.name} 
                      />
                      <AvatarFallback className="rounded-lg">
                        {activeUser.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                      <span className="truncate font-semibold text-left">
                        {activeUser.name}
                      </span>
                      <span className="truncate text-xs text-left">
                        {activeUser.role}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* Default menu items with custom handlers */}
                <DropdownMenuItem onClick={userMenuActions?.onAccountSettings}>
                  <User className="mr-2 h-4 w-4" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={userMenuActions?.onBilling}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={userMenuActions?.onNotifications}>
                  <Bell className="mr-2 h-4 w-4" />
                  Notifications
                </DropdownMenuItem>
                
                {/* Custom menu actions */}
                {userMenuActions?.customActions && userMenuActions.customActions.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    {userMenuActions.customActions.map((action, index) => {
                      const ActionIcon = action.icon ? getIconComponent(action.icon) : null;
                      return (
                        <DropdownMenuItem key={index} onClick={action.onClick}>
                          {ActionIcon && <ActionIcon className="mr-2 h-4 w-4" />}
                          {action.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={userMenuActions?.onLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
})

PresetSidebar.displayName = "PresetSidebar"

export { type SidebarConfig, type PresetSidebarProps }
